/**
 * Plugin enhancing native "vue-router" scroll behavior
 *
 *    # Features
 *
 *      Allows to preserve scroll position when:
 *        - navigating between routes
 *        - navigating within a group of routes
 *        - navigating between routes with different policies
 *        - navigating between routes with different groups
 *        - navigating between routes with different policies and groups
 *
 *    # Usage
 *
 *      To use this plugin, you need to add the following meta to your routes:
 *
 *      ```json
 *      {
 *        "meta": {
 *          "scrollPolicy": "preserve-when-involved",
 *          "scrollGroup": "my-group"
 *        }
 *      }
 *      ```
 *
 *      The `scrollPolicy` can be one of the following:
 *      - `default`: the default behavior
 *      - `preserve-when-involved`: preserve scroll position when navigating between routes
 *      - `preserve-within-group`: preserve scroll position when navigating within a group of routes
 *
 *      The `scrollGroup` is an optional string that can be used to group routes together.
 *      If a route has a `scrollGroup` and another route has the same `scrollGroup`,
 *      the scroll position will be preserved when navigating between these routes.
 *
 *      If a route has a `scrollPolicy` and another route has the same `scrollPolicy`,
 *      the scroll position will be preserved when navigating between these routes.
 *
 *      If a route has a `scrollPolicy` and another route has the same `scrollPolicy` and the same `scrollGroup`,
 *      the scroll position will be preserved when navigating between these routes.
 */
import type { RouteLocationNormalized } from 'vue-router'
import type { ScrollPolicy, ScrollGroup } from '~/types/scroll-behavior.client'

type ScrollPosition = {
  left: number
  top: number
}

function getPolicy(route: RouteLocationNormalized): ScrollPolicy {
  return (route.meta.scrollPolicy as ScrollPolicy | undefined) ?? 'default'
}

function getGroup(route: RouteLocationNormalized): ScrollGroup | undefined {
  return route.meta.scrollGroup as ScrollGroup | undefined
}

function shouldPreserveScroll(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized
): boolean {
  const toPolicy = getPolicy(to)
  const fromPolicy = getPolicy(from)

  const preserveWhenInvolved
    = toPolicy === 'preserve-when-involved'
    || fromPolicy === 'preserve-when-involved'

  const toGroup = getGroup(to)
  const fromGroup = getGroup(from)

  const preserveWithinGroup
    = toPolicy === 'preserve-within-group'
    && fromPolicy === 'preserve-within-group'
    && !!toGroup
    && toGroup === fromGroup

  return preserveWhenInvolved || preserveWithinGroup
}

export default defineNuxtPlugin((nuxtApp) => {
  const router = useRouter()
  let pendingScroll: ScrollPosition | null = null
  let restoreToken = 0

  const restore = (scroll: ScrollPosition) => {
    window.scrollTo({
      left: scroll.left,
      top: scroll.top,
      behavior: 'auto'
    })
  }

  // Some pages reset scroll after route resolution. Restore in multiple phases
  // so "open sidebar" and "close sidebar" flows are both covered.
  const scheduleRestore = () => {
    if (!pendingScroll)
      return

    const token = ++restoreToken
    const scroll = pendingScroll

    const runIfCurrent = () => {
      if (token !== restoreToken || !pendingScroll)
        return
      restore(scroll)
    }

    requestAnimationFrame(runIfCurrent)
    setTimeout(runIfCurrent, 0)
    setTimeout(runIfCurrent, 60)
  }

  router.beforeEach((to, from, next) => {
    if (to.hash || !shouldPreserveScroll(to, from)) {
      pendingScroll = null
      next()
      return
    }

    pendingScroll = {
      left: window.scrollX,
      top: window.scrollY
    }

    next()
  })

  router.afterEach((_to, _from, failure) => {
    if (failure || !pendingScroll)
      return

    scheduleRestore()
  })

  nuxtApp.hook('page:finish', () => {
    if (!pendingScroll)
      return

    scheduleRestore()
  })
})
